import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import {
    useCrmCancelTrade,
    useCrmCloseTrade,
    useCrmPartialCloseTrade,
    useCrmReopenTrade,
    useCrmTrades,
    useCrmUpdateTrade,
} from '@/api/trades/use-trades';
import { Trade } from '@/models/trade';
import { useUrlState } from '@/hooks/shared';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useSnackbar } from 'notistack';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import InlineEditableCell from '@/components/shared/InlineEditableCell';
import RealTimePrice from '@/components/shared/real-time-price/RealTimePrice';
import { formatInstrumentPrice, formatAmount, formatNumber } from '@/utils/formatters';
import {
    CheckCircle,
    Clock,
    DollarSign,
    Download,
    Minus,
    RefreshCw,
    Shield,
    Target,
    TrendingDown,
    TrendingUp,
    User,
    X,
} from 'lucide-react';
import TradeDetailsModal from '@/pages/crm/Customers/components/TradeDetailsModal';
import { TradeEditGate } from '@/components/TradingPermissionGate';
import { EditLotEntryDialog } from '@/components/trading/EditLotEntryDialog';
import { EditStopLossTakeProfitDialog } from '@/components/trading/EditStopLossTakeProfitDialog';
import { Edit2 } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';

interface TradesListProps {
    status: 'pending' | 'open' | 'closed';
    title: string;
    description: string;
    customerId?: string;
    hideCustomerColumn?: boolean;
    showExport?: boolean;
    showRefresh?: boolean;
    /**
     * Hide the card title/description. Set this when the surrounding tab already
     * names the list, so the screen does not stack page title, tab label and card
     * title for the same table.
     */
    showHeader?: boolean;
    /** Fill the parent height and pin the table header and pagination bar. */
    stickyLayout?: boolean;
}

const TradesList: React.FC<TradesListProps> = ({
    status,
    title,
    description,
    customerId,
    hideCustomerColumn = false,
    showExport = true,
    showRefresh = true,
    showHeader = true,
    stickyLayout = false,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();

    const {
        filters,
        sort,
        pagination,
        updateFilters,
        clearFilters,
        toggleSort,
        goToPage,
        updatePagination,
    } = useUrlState({
        sort: { key: 'opened_at', direction: 'desc' },
        pagination: { page: 1, pageSize: 25 },
        filters: {}, // Don't include status and customerId in URL state
    });

    const {
        data: tradesData,
        isLoading,
        refetch,
    } = useCrmTrades({
        status,
        customerId,
        per_page: pagination.pageSize,
        filters: filters,
        sort: sort,
        pagination: pagination,
    });

    const trades = tradesData?.trades || [];
    const totalTrades = tradesData?.pagination?.total || 0;
    const totalPages = tradesData?.pagination?.last_page || 1;

    // Mutations
    const updateTradeMutation = useCrmUpdateTrade();
    const cancelTradeMutation = useCrmCancelTrade();
    const closeTradeMutation = useCrmCloseTrade();
    const partialCloseTradeMutation = useCrmPartialCloseTrade();
    const reopenTradeMutation = useCrmReopenTrade();

    // State for dialogs and editing
    const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [closingTrade, setClosingTrade] = useState<Trade | null>(null);
    const [reopeningTrade, setReopeningTrade] = useState<Trade | null>(null);
    const [viewingTrade, setViewingTrade] = useState<Trade | null>(null);
    const [closeType, setCloseType] = useState<'full' | 'partial'>('full');
    const [closeLots, setCloseLots] = useState<string>('');
    const [editingLotEntryTrade, setEditingLotEntryTrade] = useState<Trade | null>(null);
    const [editingSLTPTrade, setEditingSLTPTrade] = useState<Trade | null>(null);
    const [editingSLTPType, setEditingSLTPType] = useState<'stop_loss' | 'take_profit'>(
        'stop_loss'
    );

    const handleTradeAction = async (trade: Trade, action: string, data?: any) => {
        try {
            switch (action) {
                case 'edit':
                    await updateTradeMutation.mutateAsync({ id: trade.id, data });
                    enqueueSnackbar('Trade updated successfully', { variant: 'success' });
                    break;
                case 'cancel':
                    await cancelTradeMutation.mutateAsync({ id: trade.id, data });
                    enqueueSnackbar('Trade cancelled successfully', { variant: 'success' });
                    break;
                case 'close':
                    await closeTradeMutation.mutateAsync({ id: trade.id, data });
                    enqueueSnackbar('Trade closed successfully', { variant: 'success' });
                    break;
                case 'partial-close':
                    await partialCloseTradeMutation.mutateAsync({ id: trade.id, data });
                    enqueueSnackbar('Trade partially closed successfully', { variant: 'success' });
                    break;
                case 'reopen':
                    await reopenTradeMutation.mutateAsync({ id: trade.id, data });
                    enqueueSnackbar('Trade reopened successfully', { variant: 'success' });
                    break;
                default:
                    enqueueSnackbar('Unknown action', { variant: 'error' });
            }
        } catch (error) {
            console.error('Trade action failed:', error);
            enqueueSnackbar('Action failed', { variant: 'error' });
        }
    };

    const handleCloseTrade = async () => {
        if (!closingTrade) return;

        const action = closeType === 'full' ? 'close' : 'partial-close';
        const payload: any = {
            reason: 'manual',
        };

        // Add close_lots for partial close
        if (closeType === 'partial') {
            if (!closeLots || parseFloat(closeLots) <= 0) {
                enqueueSnackbar('Please specify the number of lots to close', {
                    variant: 'error',
                });
                return;
            }
            if (parseFloat(closeLots) >= closingTrade.lot_size) {
                enqueueSnackbar('Close lots must be less than the trade lot size', {
                    variant: 'error',
                });
                return;
            }
            payload.close_lots = parseFloat(closeLots);
        }

        try {
            // handleTradeAction already handles errors and shows snackbars
            await handleTradeAction(closingTrade, action, payload);
            // Only reset dialog state if action succeeded
            setClosingTrade(null);
            setCloseType('full');
            setCloseLots('');
        } catch (error) {
            // Error is already handled by handleTradeAction, just log it
            console.error('Close trade failed:', error);
            // Don't show another error snackbar - handleTradeAction already did
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await refetch();
            enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to refresh data', { variant: 'error' });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleExport = () => {
        enqueueSnackbar('Export functionality coming soon', { variant: 'info' });
    };

    const columns: Column<Trade>[] = useMemo(
        () => [
            ...(hideCustomerColumn
                ? []
                : [
                      {
                          id: 'customer',
                          key: 'customer_id' as keyof Trade,
                          label: 'Customer',
                          render: (_value, trade: Trade) => (
                              <div className="p-2">
                                  <div className="flex items-center gap-2">
                                      <User className="h-4 w-4 text-muted-foreground shrink-0" />
                                      <div className="min-w-0">
                                          {trade.customer_id ? (
                                              <Link
                                                  to={`/customers/${trade.customer_id}`}
                                                  className="font-medium text-blue-600 hover:underline"
                                                  onClick={e => e.stopPropagation()}
                                              >
                                                  {trade.customer?.name || 'N/A'}
                                              </Link>
                                          ) : (
                                              <div className="font-medium">
                                                  {trade.customer?.name || 'N/A'}
                                              </div>
                                          )}
                                          {trade.customer?.email && (
                                              <div className="text-sm text-muted-foreground truncate">
                                                  {trade.customer.email}
                                              </div>
                                          )}
                                      </div>
                                  </div>
                              </div>
                          ),
                      },
                  ]),
            {
                id: 'trade_id',
                key: 'id',
                label: 'Trade ID',
                render: (value, trade) => (
                    <div className="font-mono text-sm">{trade.id.substring(0, 8)}...</div>
                ),
            },
            {
                id: 'instrument',
                key: 'instrument',
                label: 'Instrument',
                render: (value, trade) => (
                    <div>
                        <div className="font-medium">{trade.instrument?.symbol}</div>
                        <div className="text-sm text-muted-foreground">
                            {trade.instrument?.base_asset?.symbol}/
                            {trade.instrument?.quote_asset?.symbol}
                        </div>
                    </div>
                ),
            },
            {
                id: 'side',
                key: 'side',
                label: 'Side',
                render: (value, trade) => (
                    <Badge variant={trade.side === 'buy' ? 'default' : 'secondary'}>
                        {trade.side === 'buy' ? (
                            <TrendingUp className="h-3 w-3 mr-1" />
                        ) : (
                            <TrendingDown className="h-3 w-3 mr-1" />
                        )}
                        {trade.side.toUpperCase()}
                    </Badge>
                ),
            },
            {
                id: 'type',
                key: 'type',
                label: 'Type',
                render: (value, trade) => (
                    <Badge variant="outline">
                        {trade.type === 'market' ? (
                            <DollarSign className="h-3 w-3 mr-1" />
                        ) : trade.type === 'limit' ? (
                            <Target className="h-3 w-3 mr-1" />
                        ) : (
                            <Shield className="h-3 w-3 mr-1" />
                        )}
                        {trade.type.toUpperCase()}
                    </Badge>
                ),
            },
            {
                id: 'lot_size',
                key: 'lot_size',
                label: 'Lots',
                width: '120px',
                render: (value, trade) => (
                    <div className="text-right font-medium">
                        <TradeEditGate
                            fallback={<span>{formatAmount(Number(trade.lot_size || 0), 5)}</span>}
                        >
                            <div
                                className="flex cursor-pointer items-center justify-end gap-1 group hover:bg-muted/50 rounded px-1 -mx-1"
                                onClick={() => setEditingLotEntryTrade(trade)}
                            >
                                <span>{formatAmount(Number(trade.lot_size || 0), 5)}</span>
                                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                            </div>
                        </TradeEditGate>
                    </div>
                ),
            },
            {
                id: 'entry_price',
                key: 'entry_price',
                label: 'Entry Price',
                width: '140px',
                render: (value, trade) => (
                    <div className="text-right font-medium">
                        <TradeEditGate
                            fallback={
                                <span>
                                    {formatInstrumentPrice(
                                        Number(trade.entry_price || 0),
                                        trade.instrument as any
                                    )}
                                </span>
                            }
                        >
                            <div
                                className="flex cursor-pointer items-center justify-end gap-1 group hover:bg-muted/50 rounded px-1 -mx-1"
                                onClick={() => setEditingLotEntryTrade(trade)}
                            >
                                <span>
                                    {formatInstrumentPrice(
                                        Number(trade.entry_price || 0),
                                        trade.instrument as any
                                    )}
                                </span>
                                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                            </div>
                        </TradeEditGate>
                    </div>
                ),
            },
            {
                id: 'current_price',
                key: 'current_price',
                label: 'Current Price',
                width: '140px',
                render: (value, trade) => (
                    <div className="text-right font-medium font-mono text-sm">
                        {trade.instrument ? (
                            <RealTimePrice
                                instrument={trade.instrument as any}
                                fallbackPrice={0}
                                className="font-mono font-medium"
                            />
                        ) : (
                            <span className="text-muted-foreground">-</span>
                        )}
                    </div>
                ),
            },
            // {
            //     id: 'stop_price',
            //     key: 'stop_price',
            //     label: 'Stop Price',
            //     width: '140px',
            //     render: (value, trade) => (
            //         <div className="text-right font-medium">
            //             <InlineEditableCell
            //                 value={trade.stop_price?.toString() || ''}
            //                 onSave={async value => {
            //                     await handleTradeAction(trade, 'edit', {
            //                         stop_price: parseFloat(value.toString()),
            //                     });
            //                 }}
            //                 type="number"
            //                 displayValue={
            //                     trade.stop_price
            //                         ? formatInstrumentPrice(
            //                               Number(trade.stop_price),
            //                               trade.instrument as any
            //                           )
            //                         : '-'
            //                 }
            //             />
            //         </div>
            //     ),
            // },
            {
                id: 'take_profit',
                key: 'take_profit_price',
                label: 'Take Profit',
                width: '140px',
                render: (value, trade) => (
                    <div className="text-right font-medium">
                        <TradeEditGate
                            fallback={
                                <span>
                                    {trade.take_profit_price
                                        ? formatInstrumentPrice(
                                              Number(trade.take_profit_price),
                                              trade.instrument as any
                                          )
                                        : '-'}
                                </span>
                            }
                        >
                            <div
                                className="flex cursor-pointer items-center justify-end gap-1 group hover:bg-muted/50 rounded px-1 -mx-1"
                                onClick={() => {
                                    setEditingSLTPTrade(trade);
                                    setEditingSLTPType('take_profit');
                                }}
                            >
                                <span>
                                    {trade.take_profit_price
                                        ? formatInstrumentPrice(
                                              Number(trade.take_profit_price),
                                              trade.instrument as any
                                          )
                                        : '-'}
                                </span>
                                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                            </div>
                        </TradeEditGate>
                    </div>
                ),
            },
            {
                id: 'stop_loss',
                key: 'stop_loss_price',
                label: 'Stop Loss',
                width: '140px',
                render: (value, trade) => (
                    <div className="text-right font-medium">
                        <TradeEditGate
                            fallback={
                                <span>
                                    {trade.stop_loss_price
                                        ? formatInstrumentPrice(
                                              Number(trade.stop_loss_price),
                                              trade.instrument as any
                                          )
                                        : '-'}
                                </span>
                            }
                        >
                            <div
                                className="flex cursor-pointer items-center justify-end gap-1 group hover:bg-muted/50 rounded px-1 -mx-1"
                                onClick={() => {
                                    setEditingSLTPTrade(trade);
                                    setEditingSLTPType('stop_loss');
                                }}
                            >
                                <span>
                                    {trade.stop_loss_price
                                        ? formatInstrumentPrice(
                                              Number(trade.stop_loss_price),
                                              trade.instrument as any
                                          )
                                        : '-'}
                                </span>
                                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                            </div>
                        </TradeEditGate>
                    </div>
                ),
            },
            {
                id: 'leverage',
                key: 'leverage',
                label: 'Leverage',
                width: '120px',
                render: (value, trade) => (
                    <div className="text-right">
                        <InlineEditableCell
                            value={trade.leverage?.toString() || ''}
                            onSave={async value => {
                                await handleTradeAction(trade, 'edit', {
                                    leverage: parseInt(value.toString()),
                                });
                            }}
                            type="number"
                        />
                    </div>
                ),
            },
            {
                id: 'pnl',
                key: 'unrealized_pnl',
                label: 'P&L',
                render: (value, trade) => {
                    const pnl = status === 'closed' ? trade.realized_pnl : trade.unrealized_pnl;
                    const pnlNumber = Number(pnl) || 0;
                    const isProfit = pnlNumber > 0;
                    return (
                        <div
                            className={`text-right font-medium flex items-center justify-end gap-1 ${
                                isProfit
                                    ? 'text-green-600'
                                    : pnlNumber < 0
                                      ? 'text-red-600'
                                      : 'text-gray-600'
                            }`}
                        >
                            {isProfit ? (
                                <TrendingUp className="h-4 w-4" />
                            ) : pnlNumber < 0 ? (
                                <TrendingDown className="h-4 w-4" />
                            ) : null}
                            {pnlNumber >= 0 ? '+' : ''}${formatAmount(pnlNumber, 5)}
                        </div>
                    );
                },
            },
            {
                id: 'margin_used',
                key: 'margin_used',
                label: 'Margin Used',
                render: (value, trade) => {
                    const marginNumber = Number(trade.margin_used || 0);
                    return (
                        <div className="text-right font-medium">
                            ${formatAmount(marginNumber, 5)}
                        </div>
                    );
                },
            },
            {
                id: 'status',
                key: 'status',
                label: 'Status',
                render: (value, trade) => (
                    <Badge
                        variant={
                            trade.status === 'pending'
                                ? 'secondary'
                                : trade.status === 'open'
                                  ? 'default'
                                  : 'outline'
                        }
                    >
                        {trade.status === 'pending' ? (
                            <Clock className="h-3 w-3 mr-1" />
                        ) : trade.status === 'open' ? (
                            <CheckCircle className="h-3 w-3 mr-1" />
                        ) : (
                            <X className="h-3 w-3 mr-1" />
                        )}
                        {trade.status.toUpperCase()}
                    </Badge>
                ),
            },
            {
                id: 'opened_at',
                key: 'opened_at',
                label: 'Opened',
                render: (value, trade) => (
                    <TradeEditGate
                        fallback={
                            trade.opened_at ? (
                                <div className="flex items-center gap-1">
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                    <DateTimeFormatter date={trade.opened_at} />
                                </div>
                            ) : (
                                <span className="text-muted-foreground">-</span>
                            )
                        }
                    >
                        <InlineEditableCell
                            value={trade.opened_at || ''}
                            onSave={async val => {
                                await handleTradeAction(trade, 'edit', {
                                    opened_at: val || null,
                                });
                            }}
                            type="datetime-local"
                        />
                    </TradeEditGate>
                ),
            },
            {
                id: 'closed_at',
                key: 'closed_at',
                label: 'Closed',
                render: (value, trade) =>
                    trade.closed_at ? (
                        <div className="flex items-center gap-1">
                            <X className="h-4 w-4 text-muted-foreground" />
                            <DateTimeFormatter date={trade.closed_at} />
                        </div>
                    ) : (
                        <span className="text-muted-foreground">-</span>
                    ),
            },
            {
                id: 'actions',
                key: 'actions',
                label: 'Actions',
                render: (value, trade) => (
                    <div className="flex items-center gap-1">
                        {/* Status-specific actions */}
                        {status === 'pending' && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleTradeAction(trade, 'cancel')}
                                disabled={cancelTradeMutation.isPending}
                                title="Cancel trade"
                            >
                                Cancel
                            </Button>
                        )}

                        {status === 'open' && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setClosingTrade(trade)}
                                title="Close trade"
                            >
                                Close
                            </Button>
                        )}

                        {status === 'closed' && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setReopeningTrade(trade)}
                                title="Reopen trade"
                            >
                                Reopen
                            </Button>
                        )}

                        {/* View details button */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setViewingTrade(trade)}
                            title="View trade details"
                        >
                            View
                        </Button>
                    </div>
                ),
            },
        ],
        [
            status,
            hideCustomerColumn,
            updateTradeMutation.isPending,
            cancelTradeMutation.isPending,
            partialCloseTradeMutation.isPending,
        ]
    );

    // Column preferences with localStorage persistence
    // Include customerId and hideCustomerColumn in storage key to ensure proper isolation per tab/context
    const storageKey = useMemo(
        () =>
            `trades-list-${status}${customerId ? `-customer-${customerId}` : ''}${hideCustomerColumn ? '-no-customer-col' : ''}-visible-columns`,
        [status, customerId, hideCustomerColumn]
    );
    const { visibleColumns, handleColumnToggle, handleResetColumns } = useColumnPreferences({
        storageKey,
        defaultColumns: columns.map(col => col.id),
        columns,
    });

    const filterFields = useMemo(
        () => [
            { key: 'customer_id', label: 'Customer', type: 'select' as const },
            { key: 'instrument_id', label: 'Instrument', type: 'select' as const },
            { key: 'side', label: 'Side', type: 'select' as const },
            { key: 'type', label: 'Type', type: 'select' as const },
            { key: 'leverage', label: 'Leverage', type: 'number' as const },
        ],
        []
    );

    const presets = useMemo(
        () => [
            {
                id: 'all',
                name: 'All Trades',
                filters: {},
            },
            {
                id: 'today',
                name: 'Today',
                filters: {
                    opened_at: new Date().toISOString().split('T')[0],
                },
            },
            {
                id: 'this_week',
                name: 'This Week',
                filters: {
                    opened_at_from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                        .toISOString()
                        .split('T')[0],
                },
            },
            {
                id: 'this_month',
                name: 'This Month',
                filters: {
                    opened_at_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                        .toISOString()
                        .split('T')[0],
                },
            },
            {
                id: 'high_leverage',
                name: 'High Leverage (10x+)',
                filters: {
                    leverage_min: 10,
                },
            },
            {
                id: 'profitable',
                name: 'Profitable Trades',
                filters: {
                    pnl_min: 0.01,
                },
            },
            {
                id: 'losing',
                name: 'Losing Trades',
                filters: {
                    pnl_max: -0.01,
                },
            },
        ],
        []
    );

    // No separate loading return: the table renders skeleton rows from `isLoading`,
    // which keeps the layout stable instead of swapping in a centred text line.
    const canExport = showExport && hasPermission(ModulePermission.TRADES, PermissionAction.EXPORT);
    const hasToolbar = showRefresh || canExport;

    const toolbar = (
        <div className="flex items-center gap-2">
            {showRefresh && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    title="Refresh data"
                    aria-label={`Refresh ${title.toLowerCase()}`}
                >
                    <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
                </Button>
            )}
            {canExport && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExport}
                    title="Export data"
                    aria-label={`Export ${title.toLowerCase()}`}
                >
                    <Download className="h-4 w-4" />
                </Button>
            )}
        </div>
    );

    return (
        <div className={cn('flex flex-col gap-4', stickyLayout && 'min-h-0 flex-1')}>
            {!showHeader && hasToolbar && (
                <div className="flex shrink-0 justify-end">{toolbar}</div>
            )}
            <Card
                className={cn(
                    'border-0 bg-transparent shadow-none',
                    stickyLayout && 'flex min-h-0 flex-1 flex-col'
                )}
            >
                {showHeader && (
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>{title}</CardTitle>
                                <p className="text-sm text-muted-foreground">{description}</p>
                            </div>
                            {toolbar}
                        </div>
                    </CardHeader>
                )}
                <CardContent
                    className={cn(
                        'p-0',
                        stickyLayout && 'flex min-h-0 flex-1 flex-col',
                        showHeader && 'pt-0'
                    )}
                >
                    <GenericDataTable
                        data={trades}
                        columns={columns as any}
                        visibleColumns={visibleColumns}
                        filters={filters}
                        filterFields={filterFields}
                        presets={presets}
                        selectedItems={selectedTrades}
                        onSelectionChange={setSelectedTrades}
                        getItemId={item => item.id}
                        sortConfig={sort as { key: keyof Trade; direction: 'asc' | 'desc' }}
                        onSort={toggleSort}
                        totalItems={totalTrades}
                        totalPages={totalPages}
                        currentPage={pagination.page}
                        pageSize={pagination.pageSize}
                        onPageChange={goToPage}
                        onPageSizeChange={pageSize => updatePagination({ pageSize })}
                        onFilterChange={updateFilters}
                        onClearFilters={clearFilters}
                        onLoadPreset={preset => {
                            const presetData = presets.find(p => p.id === preset.id);
                            if (presetData) {
                                updateFilters(presetData.filters);
                            }
                        }}
                        onColumnToggle={handleColumnToggle}
                        onResetColumns={handleResetColumns}
                        isLoading={isLoading}
                        stickyLayout={stickyLayout}
                    />
                </CardContent>
            </Card>

            {/* Close Trade Dialog */}
            <Dialog open={!!closingTrade} onOpenChange={() => setClosingTrade(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Minus className="h-5 w-5" />
                            Close Trade
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="closeType">Close Type</Label>
                            <RadioGroup
                                value={closeType}
                                onValueChange={value => setCloseType(value as 'full' | 'partial')}
                            >
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="full" id="full" />
                                    <Label htmlFor="full">Full Close</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="partial" id="partial" />
                                    <Label htmlFor="partial">Partial Close</Label>
                                </div>
                            </RadioGroup>
                        </div>

                        {closeType === 'partial' && closingTrade && (
                            <div>
                                <Label htmlFor="closeLots">Lots to Close</Label>
                                <Input
                                    id="closeLots"
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={closingTrade.lot_size - 0.01}
                                    value={closeLots}
                                    onChange={e => setCloseLots(e.target.value)}
                                    placeholder={`Max: ${formatAmount(Number(closingTrade.lot_size || 0), 5)} lots`}
                                />
                                <p className="text-sm text-muted-foreground mt-1">
                                    Trade lot size:{' '}
                                    {formatAmount(Number(closingTrade.lot_size || 0), 5)} lots
                                </p>
                            </div>
                        )}

                        <div className="flex justify-end space-x-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setClosingTrade(null);
                                    setCloseType('full');
                                    setCloseLots('');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleCloseTrade}>
                                {closeType === 'full' ? 'Close Trade' : 'Partial Close'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Reopen Trade Dialog */}
            <Dialog open={!!reopeningTrade} onOpenChange={() => setReopeningTrade(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5" />
                            Reopen Trade
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm text-muted-foreground">
                                This will reopen the closed trade and set it back to open status.
                                The trade will use its original entry price and current market price
                                for PnL calculation.
                            </p>
                        </div>
                        <div className="flex justify-end space-x-2">
                            <Button variant="outline" onClick={() => setReopeningTrade(null)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={() => {
                                    if (reopeningTrade) {
                                        handleTradeAction(reopeningTrade, 'reopen', {
                                            reason: 'manual',
                                        });
                                        setReopeningTrade(null);
                                    }
                                }}
                            >
                                Reopen Trade
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* View Trade Details Dialog */}
            <TradeDetailsModal
                trade={viewingTrade as any}
                isOpen={!!viewingTrade}
                onClose={() => setViewingTrade(null)}
            />

            {/* Edit Lot & Entry Price Dialog */}
            <EditLotEntryDialog
                trade={editingLotEntryTrade}
                open={!!editingLotEntryTrade}
                onClose={() => setEditingLotEntryTrade(null)}
                onSave={async data => {
                    if (editingLotEntryTrade) {
                        await handleTradeAction(editingLotEntryTrade, 'edit', data);
                    }
                }}
            />

            {/* Edit Stop Loss / Take Profit Dialog */}
            <EditStopLossTakeProfitDialog
                trade={editingSLTPTrade}
                type={editingSLTPType}
                open={!!editingSLTPTrade}
                onClose={() => {
                    setEditingSLTPTrade(null);
                }}
                onSave={async data => {
                    if (editingSLTPTrade) {
                        await handleTradeAction(editingSLTPTrade, 'edit', data);
                    }
                }}
            />
        </div>
    );
};

export default TradesList;
